import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useSiteSelector } from '@/hooks/useSiteSelector';
import { useGlobalRealtime } from '@/hooks/useGlobalRealtime';
import { fetchSiteManagerStats } from '@/hooks/queries/useSiteManagerStatsQuery';
import { useAuthorizedSiteIds } from '@/hooks/queries/useAuthorizedSiteIds';
import { fetchSiteStaticDataFromEdge } from '@/hooks/queries/useSiteStaticData';

// Prefetch configuration for site manager data
const PREFETCH_CONFIG = {
  siteDetail: { staleTime: 1000 * 60 * 5 }, // 5 minutes
  chefStats: { staleTime: 1000 * 60 * 2 }, // 2 minutes
  budgetAnalysis: { staleTime: 1000 * 60 * 10 }, // 10 minutes
  previsionnel: { staleTime: 1000 * 60 * 10 }, // 10 minutes
  planning: { staleTime: 1000 * 60 * 5 }, // 5 minutes
  incidentsRate: { staleTime: 1000 * 60 * 5 }, // 5 minutes
  siteStaticData: { staleTime: 1000 * 60 * 30 }, // 30 minutes
};

/**
 * Hook to handle all prefetching logic for site manager role.
 * Extracted from SiteManagerLayout for cleaner architecture.
 */
export function useSiteManagerPrefetch() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { selectedSite } = useSiteSelector();
  const selectedSiteId = selectedSite?.id;
  const { siteIds } = useAuthorizedSiteIds();

  // Subscribe to realtime updates for the selected site
  useGlobalRealtime(selectedSiteId, { enabled: !!selectedSiteId });

  // Prefetch site static data for ALL accessible sites as soon as auth is ready
  // This eliminates cold-start delays when navigating between sites
  useEffect(() => {
    if (!user?.id || siteIds.length === 0) return;

    // Prefetch static data for all accessible sites (runs in background)
    siteIds.forEach(siteId => {
      queryClient.prefetchQuery({
        queryKey: ['site-static-data', siteId],
        queryFn: () => fetchSiteStaticDataFromEdge(siteId),
        staleTime: PREFETCH_CONFIG.siteStaticData.staleTime,
      });
    });
  }, [user?.id, siteIds, queryClient]);

  // Prefetch critical data when user is authenticated and site selected
  useEffect(() => {
    if (!user?.id || !selectedSiteId) return;

    // Prefetch chef stats for dashboard
    queryClient.prefetchQuery({
      queryKey: ['chefStats', user.id, selectedSiteId, 'week'],
      queryFn: () => fetchSiteManagerStats(user.id, selectedSiteId, 'week'),
      staleTime: PREFETCH_CONFIG.chefStats.staleTime,
    });
  }, [user?.id, selectedSiteId, queryClient]);

  // Prefetch site-specific data when site changes
  useEffect(() => {
    if (!selectedSiteId) return;

    // These queries will be fetched when the user navigates to the relevant pages
    // We prefetch them here to improve perceived performance
    queryClient.prefetchQuery({
      queryKey: ['site-detail', selectedSiteId],
      staleTime: PREFETCH_CONFIG.siteDetail.staleTime,
    });

    queryClient.prefetchQuery({
      queryKey: ['site-budget-analysis', selectedSiteId],
      staleTime: PREFETCH_CONFIG.budgetAnalysis.staleTime,
    });

    queryClient.prefetchQuery({
      queryKey: ['site-previsionnel', selectedSiteId],
      staleTime: PREFETCH_CONFIG.previsionnel.staleTime,
    });

    queryClient.prefetchQuery({
      queryKey: ['site-planning', selectedSiteId],
      staleTime: PREFETCH_CONFIG.planning.staleTime,
    });
  }, [selectedSiteId, queryClient]);

  return { selectedSiteId };
}
